import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { AgentUiActionId, ResolvedAgentUiAction } from '@hector/shared-types';
import { apiClient } from '@/lib/apiClient';
import { useCurrentCabinet } from '@/hooks/cabinet/useCabinetCore';
import { useCabinetMemberMe } from '@/hooks/useCabinetMemberMe';
import { DocumentViewerService } from '@/services/documentViewerService';
import { fetchBordereauDocuments, bordereauDocsQueryKey } from '@/features/bordereau/hooks/useBordereauDocuments';
import { useBordereauStore } from '@/features/bordereau/hooks/useBordereauStore';
import { isWizardExitAction, openBordereauSelectionForStamping, selectNumberedPieces } from './bordereauHandler';
import { previewDocuments } from './documentPreviewHandler';
import {
  buildCaseAnalysisDestination,
  buildHelpCenterDestination,
  buildSettingsDestination,
  dispatchAgentUiAction,
  type AgentUiActionHandlers,
} from './dispatcher';
import { createAgentUiActionHandoffState, type CaseBoundGlobalAgentUiAction } from './routeHandoff';

const CASE_CAPABILITIES = [
  'documents.preview',
  'bordereau.open_selection_for_stamping',
  'case_analysis.open_section',
] as const satisfies readonly AgentUiActionId[];
const CASELESS_CAPABILITIES = ['settings.open_section', 'help.open_center'] as const satisfies readonly AgentUiActionId[];

export interface AgentUiActionHostOptions {
  /** Current selected turn context. Case actions are omitted while it is absent. */
  readonly selectedCaseId: string | null;
  /** Mounted CaseView boundary. Another case must be reached through a handoff. */
  readonly routeCaseId?: string;
  /** `/assistant` owns no case surface, so all case actions use router state. */
  readonly alwaysHandoffCaseActions?: boolean;
}

export interface ExecutableCapabilitiesInput {
  readonly selectedCaseId: string | null;
  readonly cabinetId: string | null;
}

export function getExecutableAgentUiActionCapabilities(
  input: ExecutableCapabilitiesInput,
): readonly AgentUiActionId[] {
  return input.selectedCaseId && input.cabinetId
    ? [...CASE_CAPABILITIES, ...CASELESS_CAPABILITIES]
    : [...CASELESS_CAPABILITIES];
}

/** Wires the global registry to React Router, TanStack Query and the two UI stores. */
export function useAgentUiActionHost(options: AgentUiActionHostOptions) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentCabinetQuery = useCurrentCabinet();
  const cabinetMemberQuery = useCabinetMemberMe();
  const { data: currentCabinet } = currentCabinetQuery;
  const cabinetId = currentCabinet?.id ?? null;
  // The cabinet query is disabled until membership resolves, so its pending
  // flag alone cannot distinguish "not started yet" from "no cabinet".
  const uiActionHostStatus = cabinetMemberQuery.isPending
    || (Boolean(cabinetMemberQuery.data?.cabinet_id) && currentCabinetQuery.isPending)
    ? 'loading'
    : 'ready';
  const capabilities = useMemo(
    () => getExecutableAgentUiActionCapabilities({
      selectedCaseId: options.selectedCaseId,
      cabinetId,
    }),
    [cabinetId, options.selectedCaseId],
  );

  const mustHandoff = useCallback((action: CaseBoundGlobalAgentUiAction): boolean => (
    options.alwaysHandoffCaseActions === true
    || (options.routeCaseId !== undefined && options.routeCaseId !== action.caseId)
  ), [options.alwaysHandoffCaseActions, options.routeCaseId]);

  const handoff = useCallback((action: CaseBoundGlobalAgentUiAction): void => {
    navigate(`/cases/${action.caseId}`, { state: createAgentUiActionHandoffState(action) });
  }, [navigate]);

  const handlers = useMemo<AgentUiActionHandlers>(() => ({
    'documents.preview': async (action) => {
      if (mustHandoff(action)) return handoff(action);
      return previewDocuments(action, {
        cabinetId,
        get: (path) => apiClient.get(path),
        canPreviewDocument: DocumentViewerService.canPreviewDocument.bind(DocumentViewerService),
        viewDocuments: DocumentViewerService.viewDocuments.bind(DocumentViewerService),
      });
    },
    'bordereau.open_selection_for_stamping': async (action) => {
      if (mustHandoff(action)) return handoff(action);
      // `fetchBordereauDocuments` normally shares a short-lived cache with the panel.
      // An agent button is a commit boundary, so invalidate first to force click-time rows.
      await queryClient.invalidateQueries({ queryKey: bordereauDocsQueryKey(action.caseId) });
      const store = useBordereauStore.getState();
      return openBordereauSelectionForStamping(action, {
        cabinetId,
        fetchDocuments: (caseId) => fetchBordereauDocuments(queryClient, caseId),
        openPanel: store.openPanel,
      });
    },
    'case_analysis.open_section': async (action) => {
      if (mustHandoff(action)) return handoff(action);
      navigate(buildCaseAnalysisDestination(action.caseId, action.section));
    },
    'settings.open_section': async (action) => {
      navigate(buildSettingsDestination(action.section));
    },
    'help.open_center': async () => {
      navigate(buildHelpCenterDestination());
    },
  }), [cabinetId, handoff, mustHandoff, navigate, queryClient]);

  const onUiAction = useCallback(async (action: ResolvedAgentUiAction) => {
    if (!isWizardExitAction(action)) return dispatchAgentUiAction(action, { capabilities, handlers });
    // Le wizard exécute ces deux actions lui-même et ne passe jamais par cet hôte.
    // Le handler direct, sans dispatcher : dans un fil qui se recharge, `selectedCaseId`
    // est encore nul et les capacités vides, alors que l'action porte déjà son dossier.
    await queryClient.invalidateQueries({ queryKey: bordereauDocsQueryKey(action.caseId) });
    const rows = await fetchBordereauDocuments(queryClient, action.caseId);
    return handlers['bordereau.open_selection_for_stamping'](selectNumberedPieces(action, rows));
  }, [capabilities, handlers, queryClient]);

  return { uiActionCapabilities: capabilities, uiActionHostStatus, onUiAction } as const;
}
